import { Canvas } from "@react-three/fiber";
import {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { LaneWorld } from "./game/LaneWorld";
import { GaragePreview } from "./game/GaragePreview";
import { equippedVehicle, useGame } from "./game/store";
import { GAME, IAP, COIN_PACKS, STARTER_PACK_COINS, WELCOME_COINS } from "./game/constants";
import { VEHICLES, type VehicleId } from "./game/vehicles";
import { STAGES, isStageId, type StageId } from "./game/stages";
import { isRunSeed } from "./game/rng";
import {
  DIFFICULTIES,
  DIFFICULTY_LIST,
  isDifficultyUnlocked,
  speedGear,
  speedProgress,
  type DifficultyId,
} from "./game/difficulty";
import {
  initAds,
  maybeShowInterstitial,
  showMenuBanner,
  hideMenuBanner,
  playRewardedAd,
  preloadRewardedAd,
  openAdPrivacyOptions,
  adsPrivacyOptionsRequired,
  adsCanRequest,
  adsModeLabel,
} from "./lib/ads";
import { purchaseRemoveAds, purchaseStarterPack, purchaseCoinPack, initIap, restorePurchases, iapPriceLabel, subscribeIapPrices } from "./lib/iap";
import { signInWithApple } from "./lib/appleAuth";
import {
  authenticateGameCenter,
  showGameCenter,
  syncGameCenter,
} from "./lib/gameCenter";
import {
  MISSION_REWARD,
  localDayKey,
  missionsForDay,
  streakReward,
} from "./game/dailies";
import { shareRun } from "./lib/share";
import {
  isSfxMuted,
  loadMutePreference,
  music,
  setSfxMuted,
  sfx,
} from "./lib/audio";
import {
  fetchLeaderboard,
  submitScore,
  type LeaderboardEntry,
} from "./lib/leaderboard";
import { isShotMode } from "./lib/shotMode";
import {
  cycleLocale,
  t,
  tCurrentLocaleName,
  tDiff,
  tDiffHint,
  tMission,
  tPowerBlurb,
  tPowerName,
  tStageBlurb,
  tStageName,
  tVehicleBlurb,
  useLocale,
} from "./i18n";
import "./App.css";

function iapFailCopy(reason?: string) {
  if (reason === "product_unavailable") {
    return t("iapProductUnavailable");
  }
  if (reason === "store_unavailable") {
    return t("iapStoreUnavailable");
  }
  return t("iapFailed", { reason: reason ?? "error" });
}

export default function App() {
  useLocale();
  const phase = useGame((s) => s.phase);
  const paused = useGame((s) => s.paused);
  const garageOpen = useGame((s) => s.garageOpen);
  const hydrate = useGame((s) => s.hydrate);
  const pointer = useRef<{ x: number; y: number; t: number } | null>(null);

  useEffect(() => {
    hydrate();
    loadMutePreference();
    if (!isShotMode()) {
      void initAds();
      void initIap();
    }
    window.setTimeout(() => {
      void authenticateGameCenter().then((ok) => {
        if (ok) void syncGameCenter({ runScore: 0 });
      });
    }, 1600);
    try {
      const q = new URLSearchParams(window.location.search);
      const seed = q.get("duel") || q.get("seed");
      const meters = Number(q.get("m") || q.get("meters") || 0);
      const name = q.get("n") || q.get("name") || "Rival";
      const diffRaw = q.get("d") || q.get("diff") || "medium";
      const stageRaw = q.get("s") || q.get("stage") || "neon";
      const diff =
        diffRaw === "easy" || diffRaw === "hard" || diffRaw === "medium"
          ? diffRaw
          : "medium";
      if (seed && isRunSeed(seed) && meters > 0) {
        useGame.getState().setPendingDuel({
          rivalName: name.slice(0, 16),
          rivalDistance: Math.floor(meters),
          seed,
          difficulty: diff,
          stage: isStageId(stageRaw) ? stageRaw : "neon",
          mode: "ranks",
        });
      }
    } catch {
      /* ignore bad query */
    }
  }, [hydrate]);

  useEffect(() => {
    if (!isShotMode()) return;
    document.documentElement.classList.add("asc-shot");
    const api = {
      start() {
        const g = useGame.getState();
        g.startRun({ seed: "c0ffee88" });
        useGame.setState({ invulnerableUntil: 1e15, jumps: 3 });
      },
      distance() {
        return Math.floor(useGame.getState().distance);
      },
    };
    (window as unknown as { __lanedashShot: typeof api }).__lanedashShot = api;
    return () => document.documentElement.classList.remove("asc-shot");
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) useGame.getState().pause();
    };
    document.addEventListener("visibilitychange", onVis);
    const sub = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) useGame.getState().pause();
    });
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      void sub.then((h) => h.remove());
    };
  }, []);

  // Hide native banner in-run / crash. Menu & result overlays show it
  // themselves so Garage / Ranks / Settings can dismiss it and stay tappable.
  useEffect(() => {
    if (phase !== "menu" && phase !== "result") {
      void hideMenuBanner();
    }
  }, [phase]);

  // Arcade BGM follows phase (unlock needs a user gesture first).
  useEffect(() => {
    if (isSfxMuted()) {
      music.stop();
      return;
    }
    if (phase === "playing" && !paused) {
      music.play();
    } else if (phase === "dead" || (phase === "playing" && paused)) {
      music.duck();
    } else {
      music.menu();
    }
  }, [phase, paused]);

  const haptic = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      void Haptics.impact({ style: ImpactStyle.Light }).catch(() => undefined);
    }
  }, []);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    pointer.current = { x: e.clientX, y: e.clientY, t: performance.now() };
    sfx.unlock();
    if (useGame.getState().phase === "menu" && !isSfxMuted()) {
      music.menu();
    }
  }, []);

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (useGame.getState().phase !== "playing" || useGame.getState().paused) {
        pointer.current = null;
        return;
      }
      const start = pointer.current;
      pointer.current = null;
      if (!start) return;

      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      const dt = performance.now() - start.t;

      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.15 && dt < 420) {
        const dir = (dx < 0 ? -1 : 1) as -1 | 1;
        useGame.getState().nudgeLane(dir);
        sfx.lane(dir);
        haptic();
        return;
      }

      // Swipe up = consume a jump token (must have pickups).
      if (dy < -40 && Math.abs(dy) > Math.abs(dx) * 1.1 && dt < 420) {
        if (useGame.getState().useJump()) {
          sfx.jump();
          haptic();
        }
        return;
      }

      if (Math.abs(dx) < 28 && Math.abs(dy) < 28 && dt < 320) {
        const dir = (e.clientX / window.innerWidth < 0.5 ? -1 : 1) as -1 | 1;
        useGame.getState().nudgeLane(dir);
        sfx.lane(dir);
        haptic();
      }
    },
    [haptic],
  );

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const g = useGame.getState();
      if (ev.key === "Escape" || ev.key === "p" || ev.key === "P") {
        if (g.phase === "playing") {
          ev.preventDefault();
          if (g.paused) g.resume();
          else g.pause();
        }
        return;
      }
      if (g.phase !== "playing" || g.paused) return;
      if (ev.key === "ArrowLeft" || ev.key === "a") {
        useGame.getState().nudgeLane(-1);
        sfx.lane(-1);
      }
      if (ev.key === "ArrowRight" || ev.key === "d") {
        useGame.getState().nudgeLane(1);
        sfx.lane(1);
      }
      if (ev.key === "ArrowUp" || ev.key === "w" || ev.key === " ") {
        ev.preventDefault();
        if (useGame.getState().useJump()) sfx.jump();
      }
      if (ev.key === "e" || ev.key === "E" || ev.key === "f" || ev.key === "F") {
        if (useGame.getState().usePower()) sfx.power();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div
      className="app"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        pointer.current = null;
      }}
    >
      {!garageOpen || isShotMode() ? (
        <Canvas
          camera={{ position: [0, 5.8, 11.2], fov: 50, near: 0.1, far: 110 }}
          dpr={[1, 1.5]}
          gl={{
            antialias: false,
            powerPreference: "high-performance",
            stencil: false,
            preserveDrawingBuffer: isShotMode(),
          }}
        >
          <Suspense fallback={null}>
            <LaneWorld />
          </Suspense>
        </Canvas>
      ) : null}
      <Hud />
      {phase === "playing" && <PowerButton />}
      {phase === "playing" && paused && <PauseOverlay />}
      {phase === "menu" && <MenuOverlay />}
      {phase === "dead" && <ContinueOverlay />}
      {phase === "result" && <ResultOverlay />}
      {phase === "playing" && <HintOnce />}
    </div>
  );
}

function HintOnce() {
  const phase = useGame((s) => s.phase);
  const paused = useGame((s) => s.paused);
  const practiceVehicle = useGame((s) => s.practiceVehicle);
  const [show, setShow] = useState(true);
  const power = equippedVehicle().power;

  useEffect(() => {
    if (phase !== "playing") {
      setShow(true);
      return;
    }
    return useGame.subscribe((s) => {
      if (s.distance > 55) setShow(false);
    });
  }, [phase]);

  if (!show || phase !== "playing" || paused || isShotMode()) return null;
  if (practiceVehicle) {
    return <p className="hint">{t("tryRideHint")}</p>;
  }
  return (
    <p className="hint">
      {t("swipeHint")}
      {power.type === "active" ? t("swipeHintPower") : ""}
    </p>
  );
}

function PowerButton() {
  const paused = useGame((s) => s.paused);
  const powerActiveUntil = useGame((s) => s.powerActiveUntil);
  const powerReadyAt = useGame((s) => s.powerReadyAt);
  const powerAuto = useGame((s) => s.powerAuto);
  const activatePower = useGame((s) => s.usePower);
  const setPowerAuto = useGame((s) => s.setPowerAuto);
  const vehicle = equippedVehicle();
  const [, setTick] = useState(0);
  const holdTimer = useRef<number | null>(null);
  const held = useRef(false);

  useEffect(() => {
    if (vehicle.power.type !== "active") return;
    const id = window.setInterval(() => setTick((n) => n + 1), 100);
    return () => window.clearInterval(id);
  }, [vehicle.power.type]);

  // Auto-cast when ready (only if player opted in via long-press).
  useEffect(() => {
    if (vehicle.power.type !== "active" || !powerAuto) return;
    const id = window.setInterval(() => {
      const g = useGame.getState();
      if (g.phase !== "playing" || g.paused) return;
      const now = performance.now();
      if (now >= g.powerReadyAt && now >= g.powerActiveUntil) {
        if (g.usePower()) sfx.power();
      }
    }, 200);
    return () => window.clearInterval(id);
  }, [vehicle.power.type, powerAuto]);

  if (vehicle.power.type !== "active" || paused) return null;

  const now = performance.now();
  const active = now < powerActiveUntil;
  const ready = now >= powerReadyAt;
  const cooldownMs = (vehicle.power.cooldown ?? 20) * 1000;
  const remaining = Math.max(0, powerReadyAt - now);
  const cooldownPct = ready ? 0 : Math.min(1, remaining / cooldownMs);
  const label = tPowerName(vehicle.power.id);

  const clearHold = () => {
    if (holdTimer.current != null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  return (
    <button
      type="button"
      className={`power-btn${active ? " active" : ""}${ready && !active ? " ready" : ""}${powerAuto ? " auto" : ""}`}
      disabled={!powerAuto && (!ready || active)}
      aria-label={t("powerAria", {
        label,
        auto: powerAuto ? t("powerAriaAuto") : "",
      })}
      title={t("powerHoldHint")}
      onPointerDown={(e) => {
        e.stopPropagation();
        held.current = false;
        clearHold();
        holdTimer.current = window.setTimeout(() => {
          held.current = true;
          setPowerAuto(!useGame.getState().powerAuto);
          sfx.ui();
        }, 420);
      }}
      onPointerUp={(e) => {
        e.stopPropagation();
        clearHold();
        if (held.current) return;
        if (activatePower()) sfx.power();
      }}
      onPointerCancel={() => {
        clearHold();
      }}
      onPointerLeave={() => {
        clearHold();
      }}
    >
      <span
        className="power-ring"
        style={{ ["--cd" as string]: `${cooldownPct}` }}
        aria-hidden
      />
      <span className="power-icon">⚡</span>
      <span className="power-label">
        {active
          ? t("powerOn")
          : ready
            ? powerAuto
              ? t("powerAuto")
              : label
            : `${Math.ceil(remaining / 1000)}s`}
      </span>
      {powerAuto ? <span className="power-auto-tag">{t("powerAuto")}</span> : null}
    </button>
  );
}

function PauseOverlay() {
  const resume = useGame((s) => s.resume);
  const finishToResult = useGame((s) => s.finishToResult);
  const ghostOn = useGame((s) => s.ghostOn);
  const setGhostOn = useGame((s) => s.setGhostOn);
  const [sfxOff, setSfxOff] = useState(() => isSfxMuted());

  return (
    <div
      className="pause-pop"
      role="dialog"
      aria-labelledby="pause-title"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <div className="pause-card">
        <h2 id="pause-title">{t("paused")}</h2>
        <button
          type="button"
          className="settings-row"
          onClick={() => {
            const next = !sfxOff;
            setSfxMuted(next);
            setSfxOff(next);
            if (next) music.stop();
            else music.duck();
            sfx.ui();
          }}
        >
          <span>{t("sound")}</span>
          <span className="settings-val">{sfxOff ? t("off") : t("on")}</span>
        </button>
        <button
          type="button"
          className="settings-row"
          onClick={() => {
            setGhostOn(!ghostOn);
            sfx.ui();
          }}
        >
          <span>{t("pbGhost")}</span>
          <span className="settings-val">{ghostOn ? t("on") : t("off")}</span>
        </button>
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            resume();
            sfx.ui();
            if (!isSfxMuted()) music.play();
          }}
        >
          {t("resume")}
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={() => {
            sfx.ui();
            finishToResult();
          }}
        >
          {t("endRun")}
        </button>
      </div>
    </div>
  );
}

function Hud() {
  const phase = useGame((s) => s.phase);
  const paused = useGame((s) => s.paused);
  const pause = useGame((s) => s.pause);
  const duel = useGame((s) => s.duel);
  const practiceVehicle = useGame((s) => s.practiceVehicle);
  const distRef = useRef<HTMLDivElement>(null);
  const speedRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const jumpRef = useRef<HTMLDivElement>(null);
  const coinRef = useRef<HTMLDivElement>(null);
  const lastGear = useRef(1);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (phase === "menu") {
      lastGear.current = 1;
      setFlash(false);
      return;
    }
    let raf = 0;
    let flashTimer: number | null = null;
    const loop = () => {
      const g = useGame.getState();
      const diff = DIFFICULTIES[g.difficulty];
      const gear = speedGear(diff, g.speed);
      const progress = speedProgress(diff, g.speed);
      if (distRef.current) {
        distRef.current.textContent = `${Math.floor(g.distance)} m`;
      }
      if (speedRef.current) {
        speedRef.current.textContent = `${g.speed.toFixed(0)} · G${gear}`;
      }
      if (barRef.current) {
        barRef.current.style.width = `${progress * 100}%`;
      }
      if (jumpRef.current) {
        jumpRef.current.textContent = `↑ ${g.jumps}/${GAME.maxJumpsHeld}`;
        jumpRef.current.classList.toggle("empty", g.jumps === 0);
      }
      if (coinRef.current) {
        const n =
          g.phase === "playing" || g.phase === "dead" ? g.coinsRun : g.coins;
        coinRef.current.textContent = `✦ ${n}`;
      }
      if (g.phase === "playing" && !g.paused) {
        if (gear > lastGear.current) {
          lastGear.current = gear;
          setFlash(true);
          sfx.speedUp();
          music.setDrive((gear - 1) / 4);
          if (flashTimer != null) window.clearTimeout(flashTimer);
          flashTimer = window.setTimeout(() => setFlash(false), 900);
        } else {
          lastGear.current = gear;
          music.setDrive((gear - 1) / 4);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      if (flashTimer != null) window.clearTimeout(flashTimer);
    };
  }, [phase]);

  if (phase === "menu") return null;
  return (
    <div className="hud">
      <div className="hud-left">
        {phase === "playing" && !paused && (
          <button
            type="button"
            className="pause-btn"
            aria-label={t("pause")}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => {
              e.stopPropagation();
              pause();
              sfx.ui();
            }}
          >
            II
          </button>
        )}
        <div
          className={`hud-pill hud-dist${practiceVehicle ? " practice" : ""}`}
          ref={distRef}
        >
          0 m
        </div>
        {duel ? (
          <div className="hud-pill muted">
            {t("vsHud", { name: duel.rivalName, m: duel.rivalDistance })}
          </div>
        ) : null}
      </div>
      <div className="hud-speed">
        <div className="hud-pill muted" ref={speedRef}>
          0 · G1
        </div>
        <div className="speed-bar" aria-hidden>
          <div className="speed-bar-fill" ref={barRef} style={{ width: "0%" }} />
        </div>
        {flash ? <div className="speed-flash">{t("speedUp")}</div> : null}
      </div>
      <div className="hud-right">
        <div className="hud-pill jump" ref={jumpRef}>
          ↑ 0/{GAME.maxJumpsHeld}
        </div>
        <div className="hud-pill gold" ref={coinRef}>
          ✦ 0
        </div>
      </div>
    </div>
  );
}

function InfoVignette({ kind }: { kind: "rival" | "truck" | "barrier" | "crate" | "coin" | "jump" }) {
  return (
    <span className={`info-vignette info-vignette--${kind}`} aria-hidden="true">
      {kind === "rival" && (
        <svg viewBox="0 0 40 40" width="40" height="40">
          <rect x="8" y="18" width="24" height="8" rx="2" fill="#f472b6" />
          <rect x="12" y="12" width="14" height="7" rx="2" fill="#22d3ee" />
          <circle cx="12" cy="28" r="4" fill="#94a3b8" stroke="#e879f9" strokeWidth="1.5" />
          <circle cx="28" cy="28" r="4" fill="#94a3b8" stroke="#e879f9" strokeWidth="1.5" />
          <rect x="26" y="14" width="6" height="3" rx="1" fill="#67e8f9" />
        </svg>
      )}
      {kind === "truck" && (
        <svg viewBox="0 0 40 40" width="40" height="40">
          <rect x="6" y="8" width="22" height="22" rx="2" fill="#1e40af" />
          <rect x="6" y="18" width="22" height="4" fill="#fbbf24" />
          <rect x="26" y="16" width="10" height="12" rx="1" fill="#0f172a" />
          <rect x="28" y="18" width="6" height="4" fill="#67e8f9" />
          <circle cx="12" cy="32" r="3.5" fill="#94a3b8" />
          <circle cx="30" cy="32" r="3.5" fill="#94a3b8" />
        </svg>
      )}
      {kind === "barrier" && (
        <svg viewBox="0 0 40 40" width="40" height="40">
          <rect x="4" y="16" width="32" height="10" rx="2" fill="#f97316" />
          <rect x="4" y="16" width="32" height="10" rx="2" fill="none" stroke="#fbbf24" strokeWidth="1.5" strokeDasharray="5 3" />
          <rect x="6" y="26" width="4" height="6" fill="#64748b" />
          <rect x="30" y="26" width="4" height="6" fill="#64748b" />
        </svg>
      )}
      {kind === "crate" && (
        <svg viewBox="0 0 40 40" width="40" height="40">
          <rect x="8" y="10" width="24" height="22" rx="2" fill="#b45309" stroke="#fbbf24" strokeWidth="1.5" />
          <line x1="8" y1="21" x2="32" y2="21" stroke="#fbbf24" strokeWidth="1.5" />
          <line x1="20" y1="10" x2="20" y2="32" stroke="#fbbf24" strokeWidth="1.5" />
          <line x1="10" y1="12" x2="30" y2="30" stroke="rgba(251,191,36,0.45)" strokeWidth="1" />
        </svg>
      )}
      {kind === "coin" && (
        <svg viewBox="0 0 40 40" width="40" height="40">
          <polygon
            points="20,4 26,14 36,16 28,24 30,34 20,28 10,34 12,24 4,16 14,14"
            fill="#fbbf24"
            stroke="#fde68a"
            strokeWidth="1.2"
          />
        </svg>
      )}
      {kind === "jump" && (
        <svg viewBox="0 0 40 40" width="40" height="40">
          <circle cx="20" cy="20" r="14" fill="rgba(34,211,238,0.2)" stroke="#22d3ee" strokeWidth="2" />
          <path d="M20 28 V14 M14 18 L20 12 L26 18" fill="none" stroke="#67e8f9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

function InfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="info-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-modal-title"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="info-modal"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="info-modal-close"
          aria-label={t("close")}
          onClick={onClose}
        >
          ×
        </button>
        <h2 id="info-modal-title">{t("howToPlay")}</h2>
        <p className="info-lead">{t("infoLead")}</p>

        <section className="info-section">
          <h3>{t("controls")}</h3>
          <ul className="info-list">
            <li>
              <strong>{t("swipeLR")}</strong>
              {t("swipeLRDesc")}
            </li>
            <li>
              <strong>{t("swipeUp")}</strong>
              {t("swipeUpDesc")}
            </li>
            <li>
              <strong>{t("tapPower")}</strong>
              {t("tapPowerDesc")}
            </li>
            <li>
              <strong>{t("continueInfo")}</strong>
              {t("continueInfoDesc")}
            </li>
          </ul>
        </section>

        <section className="info-section">
          <h3>{t("twoPlayers")}</h3>
          <ul className="info-list">
            <li>
              <strong>{t("hotseatInfo")}</strong>
              {t("hotseatInfoDesc", { pass: t("passPhone") })}
            </li>
            <li>
              <strong>{t("ranksDuelInfo")}</strong>
              {t("ranksDuelInfoDesc", { ranks: t("ranks"), duel: t("duel") })}
            </li>
            <li>
              <strong>{t("shareInfo")}</strong>
              {t("shareInfoDesc", { share: t("shareInvite") })}
            </li>
          </ul>
          <p className="info-twop-rule">{t("twoPlayersRule")}</p>
        </section>

        <section className="info-section">
          <h3>{t("obstacles")}</h3>
          <ul className="info-guide">
            <li>
              <InfoVignette kind="rival" />
              <div>
                <strong>{t("rivalBikes")}</strong>
                <span>{t("rivalDesc")}</span>
              </div>
            </li>
            <li>
              <InfoVignette kind="truck" />
              <div>
                <strong>{t("trucks")}</strong>
                <span>{t("truckDesc")}</span>
              </div>
            </li>
            <li>
              <InfoVignette kind="barrier" />
              <div>
                <strong>{t("barriers")}</strong>
                <span>{t("barrierDesc")}</span>
              </div>
            </li>
            <li>
              <InfoVignette kind="crate" />
              <div>
                <strong>{t("crates")}</strong>
                <span>{t("crateDesc")}</span>
              </div>
            </li>
          </ul>
        </section>

        <section className="info-section">
          <h3>{t("collectibles")}</h3>
          <ul className="info-guide">
            <li>
              <InfoVignette kind="coin" />
              <div>
                <strong>{t("coinsTitle")}</strong>
                <span>{t("coinsDesc")}</span>
              </div>
            </li>
            <li>
              <InfoVignette kind="jump" />
              <div>
                <strong>{t("jumpTokens")}</strong>
                <span>{t("jumpDesc", { n: GAME.maxJumpsHeld })}</span>
              </div>
            </li>
          </ul>
        </section>

        <p className="info-foot">
          {t("infoFoot", { garage: t("garage"), ranks: t("ranks") })}
        </p>
      </div>
    </div>
  );
}

function MenuOverlay() {
  const [panel, setPanel] = useState<
    "none" | "garage" | "shop" | "board" | "settings"
  >("none");
  const [garageTab, setGarageTab] = useState<"rides" | "worlds">("rides");
  const [infoOpen, setInfoOpen] = useState(false);
  const [browseId, setBrowseId] = useState<VehicleId | null>(null);
  const [browseStageId, setBrowseStageId] = useState<StageId | null>(null);
  const [board, setBoard] = useState<LeaderboardEntry[]>([]);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [sfxOff, setSfxOff] = useState(() => isSfxMuted());
  const [restoreMsg, setRestoreMsg] = useState<string | null>(null);
  const [garageHint, setGarageHint] = useState<string | null>(null);
  const [iapMsg, setIapMsg] = useState<string | null>(null);
  const [iapTick, setIapTick] = useState(0);
  const [appleMsg, setAppleMsg] = useState<string | null>(null);
  const [appleBusy, setAppleBusy] = useState(false);
  const [gcMsg, setGcMsg] = useState<string | null>(null);
  const [gcBusy, setGcBusy] = useState(false);
  const [ctaShake, setCtaShake] = useState(false);
  const [nameEdit, setNameEdit] = useState("");
  const startRun = useGame((s) => s.startRun);
  const pendingDuel = useGame((s) => s.pendingDuel);
  const resumeGarageVehicle = useGame((s) => s.resumeGarageVehicle);
  const clearResumeGarage = useGame((s) => s.clearResumeGarage);
  const clearPendingDuel = useGame((s) => s.clearPendingDuel);
  const coins = useGame((s) => s.coins);
  const gamesPlayed = useGame((s) => s.gamesPlayed);
  const vehiclesOwned = useGame((s) => s.vehiclesOwned);
  const equippedVehicleId = useGame((s) => s.equippedVehicle);
  const unlockVehicle = useGame((s) => s.unlockVehicle);
  const equipVehicle = useGame((s) => s.equipVehicle);
  const stagesOwned = useGame((s) => s.stagesOwned);
  const equippedStageId = useGame((s) => s.equippedStage);
  const unlockStage = useGame((s) => s.unlockStage);
  const equipStage = useGame((s) => s.equipStage);
  const removeAds = useGame((s) => s.removeAds);
  const starterOwned = useGame((s) => s.starterOwned);
  const difficulty = useGame((s) => s.difficulty);
  const setDifficulty = useGame((s) => s.setDifficulty);
  const best = useGame((s) => s.bestByDifficulty[s.difficulty]);
  const playerName = useGame((s) => s.playerName);
  const setPlayerName = useGame((s) => s.setPlayerName);
  const appleUserId = useGame((s) => s.appleUserId);
  const welcomeGiftClaimed = useGame((s) => s.welcomeGiftClaimed);
  const hydrated = useGame((s) => s.hydrated);
  const ghostOn = useGame((s) => s.ghostOn);
  const setGhostOn = useGame((s) => s.setGhostOn);
  const claimWelcomeGift = useGame((s) => s.claimWelcomeGift);
  const setGarageOpen = useGame((s) => s.setGarageOpen);

  useEffect(() => subscribeIapPrices(() => setIapTick((n) => n + 1)), []);

  useEffect(() => {
    setNameEdit(playerName);
  }, [playerName]);

  useEffect(() => {
    setGarageOpen(panel === "garage");
    return () => setGarageOpen(false);
  }, [panel, setGarageOpen]);

  useEffect(() => {
    if (panel === "none") void showMenuBanner(removeAds);
    else void hideMenuBanner();
  }, [panel, removeAds]);

  const previewId = browseId ?? equippedVehicleId;
  const previewIndex = Math.max(
    0,
    VEHICLES.findIndex((v) => v.id === previewId),
  );
  const preview = VEHICLES[previewIndex] ?? VEHICLES[0];

  const stagePreviewId = browseStageId ?? equippedStageId;
  const stageIndex = Math.max(
    0,
    STAGES.findIndex((s) => s.id === stagePreviewId),
  );
  const stagePreview = STAGES[stageIndex] ?? STAGES[0];

  const openGarage = () => {
    setBrowseId(equippedVehicleId);
    setBrowseStageId(equippedStageId);
    setGarageTab("rides");
    setPanel("garage");
    sfx.ui();
  };

  useEffect(() => {
    if (!resumeGarageVehicle) return;
    setBrowseId(resumeGarageVehicle);
    setBrowseStageId(equippedStageId);
    setGarageTab("rides");
    setPanel("garage");
    clearResumeGarage();
  }, [resumeGarageVehicle, equippedStageId, clearResumeGarage]);

  const launchPractice = (id: VehicleId) => {
    sfx.start();
    music.setDrive(0);
    startRun({ practiceVehicle: id });
  };

  const browseVehicle = (dir: -1 | 1) => {
    const next = (previewIndex + dir + VEHICLES.length) % VEHICLES.length;
    setBrowseId(VEHICLES[next].id);
    setGarageHint(null);
    sfx.ui();
  };

  const browseStage = (dir: -1 | 1) => {
    const next = (stageIndex + dir + STAGES.length) % STAGES.length;
    setBrowseStageId(STAGES[next].id);
    setGarageHint(null);
    sfx.ui();
  };

  useEffect(() => {
    if (panel !== "board") return;
    let cancelled = false;
    setLoadingBoard(true);
    void fetchLeaderboard(difficulty, 20).then((rows) => {
      if (cancelled) return;
      setBoard(rows);
      setLoadingBoard(false);
    });
    return () => {
      cancelled = true;
    };
  }, [panel, difficulty]);

  useEffect(() => {
    setGarageHint(null);
    setCtaShake(false);
  }, [preview.id, stagePreview.id, panel, garageTab]);

  useEffect(() => {
    if (!infoOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setInfoOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [infoOpen]);

  const coinBoostLabel = (mult: number) => {
    if (mult <= 1) return t("baseCoins");
    const pct = Math.round((mult - 1) * 100);
    return t("extraCoins", { pct });
  };

  return (
    <div
      className={`overlay menu${panel !== "none" ? " panel-open" : ""}${panel === "garage" ? " garage-open" : ""}`}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="info-btn"
        aria-label={t("howToPlay")}
        title={t("howToPlay")}
        onClick={() => {
          setInfoOpen(true);
          sfx.ui();
        }}
      >
        i
      </button>

      {infoOpen && (
        <InfoModal
          onClose={() => {
            setInfoOpen(false);
            sfx.ui();
          }}
        />
      )}

      {hydrated && !welcomeGiftClaimed && (
        <div
          className="welcome-pop"
          role="dialog"
          aria-labelledby="welcome-gift-title"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
        >
          <div className="welcome-card">
            <p className="welcome-kicker">{t("launchBonus")}</p>
            <h2 id="welcome-gift-title">{t("welcomeTitle", { n: WELCOME_COINS })}</h2>
            <p>{t("welcomeBody")}</p>
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                if (claimWelcomeGift()) sfx.coin();
                else sfx.ui();
              }}
            >
              {t("claimCoins", { n: WELCOME_COINS })}
            </button>
          </div>
        </div>
      )}

      <div className="menu-main">
      {panel !== "garage" && panel !== "shop" && (
        <div className={`brand${panel !== "none" ? " compact" : ""}`}>
          <h1>3Dash</h1>
          <p className="tagline">{t("tagline")}</p>
        </div>
      )}
      <div className="stats-row">
        {panel === "garage" || panel === "shop" ? (
          <span className="gold">✦ {coins}</span>
        ) : (
          <>
            <span>
              {t("bestMeters", { n: best, diff: tDiff(difficulty) })}
            </span>
            <span className="gold">✦ {coins}</span>
          </>
        )}
      </div>

      {panel === "none" && (
        <>
          {pendingDuel ? (
            <div className="duel-banner">
              <p>
                {t("duelBanner", {
                  name: pendingDuel.rivalName,
                  m: pendingDuel.rivalDistance,
                  diff: tDiff(pendingDuel.difficulty),
                  stage: tStageName(pendingDuel.stage),
                })}
              </p>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => {
                  clearPendingDuel();
                  sfx.ui();
                }}
              >
                {t("cancel")}
              </button>
            </div>
          ) : null}
          <DailyBlock />
          <div className="diff-row">
            {DIFFICULTY_LIST.map((d) => {
              const unlocked = isDifficultyUnlocked(d.id, gamesPlayed);
              const need = Math.max(0, d.unlockRuns - gamesPlayed);
              return (
                <button
                  key={d.id}
                  type="button"
                  className={`diff ${difficulty === d.id ? "on" : ""}${unlocked && !pendingDuel ? "" : " locked"}`}
                  aria-disabled={!unlocked || Boolean(pendingDuel)}
                  onClick={() => {
                    if (pendingDuel) return;
                    if (!unlocked) {
                      sfx.deny();
                      return;
                    }
                    setDifficulty(d.id);
                    sfx.ui();
                  }}
                >
                  <span className="diff-label">{tDiff(d.id)}</span>
                  <span className="diff-hint">
                    {unlocked
                      ? tDiffHint(d.id)
                      : need === 1
                        ? t("runLeft")
                        : t("runsLeft", { n: need })}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="diff-note">
            {pendingDuel
              ? t("difficultyLocked")
              : t("diffNote")}
          </p>
        </>
      )}

      {panel === "settings" && (
        <div className="settings-panel">
          <button
            type="button"
            className="settings-row"
            onClick={() => {
              cycleLocale();
              sfx.ui();
            }}
          >
            <span>{t("language")}</span>
            <span className="settings-val">{tCurrentLocaleName()}</span>
          </button>
          <button
            type="button"
            className="settings-row"
            onClick={() => {
              const next = !sfxOff;
              setSfxMuted(next);
              setSfxOff(next);
              if (!next) {
                sfx.unlock();
                music.menu();
                sfx.ui();
              } else {
                music.stop();
              }
            }}
          >
            <span>{t("muteSfx")}</span>
            <span className="settings-val">{sfxOff ? t("muted") : t("on")}</span>
          </button>
          <button
            type="button"
            className="settings-row"
            onClick={() => {
              setGhostOn(!ghostOn);
              sfx.ui();
            }}
          >
            <span>{t("pbGhost")}</span>
            <span className="settings-val">{ghostOn ? t("on") : t("off")}</span>
          </button>
          <div className="settings-row" aria-live="polite">
            <span>{t("ads")}</span>
            <span className="settings-val">
              {removeAds
                ? t("adsOff")
                : !adsCanRequest()
                  ? t("adsConsentOff", { mode: adsModeLabel() })
                  : adsModeLabel()}
            </span>
          </div>
          <label className="name-field">
            <span>{t("pseudoRanks")}</span>
            <input
              value={nameEdit}
              maxLength={16}
              placeholder="Courier"
              enterKeyHint="done"
              onChange={(e) => setNameEdit(e.target.value)}
              onBlur={() => {
                const clean = nameEdit.trim();
                if (clean) {
                  setPlayerName(clean);
                  sfx.ui();
                } else {
                  setNameEdit(playerName);
                }
              }}
            />
          </label>
          <p className="settings-hint">{t("pseudoHint")}</p>
          <button
            type="button"
            className="settings-row"
            disabled={gcBusy}
            onClick={() => {
              void (async () => {
                setGcBusy(true);
                setGcMsg(t("gcOpening"));
                const res = await showGameCenter("leaderboards");
                setGcBusy(false);
                if (res.ok) {
                  setGcMsg(null);
                  sfx.ui();
                  return;
                }
                setGcMsg(
                  res.reason === "web-noop" ? t("gcIos") : t("gcSignIn"),
                );
                sfx.deny();
              })();
            }}
          >
            <span>{t("gameCenter")}</span>
            <span className="settings-val">{gcBusy ? "…" : t("open")}</span>
          </button>
          <p className="settings-hint">{t("gcHint")}</p>
          {gcMsg ? <p className="settings-hint">{gcMsg}</p> : null}
          {appleUserId ? (
            <>
              <button type="button" className="settings-row" disabled>
                <span>{t("signInApple")}</span>
                <span className="settings-val">{t("linked")}</span>
              </button>
              <p className="settings-hint">{t("appleLinkedHint")}</p>
            </>
          ) : (
            <>
              <button
                type="button"
                className="settings-row"
                disabled={appleBusy}
                onClick={() => {
                  void (async () => {
                    setAppleBusy(true);
                    setAppleMsg(t("appleWaiting"));
                    const res = await signInWithApple();
                    setAppleBusy(false);
                    if (res.ok) {
                      setAppleMsg(
                        res.reason === "web-mock"
                          ? t("appleLinkedWeb")
                          : t("appleLinked"),
                      );
                      sfx.ui();
                      return;
                    }
                    if (res.reason?.startsWith("cancelled")) {
                      setAppleMsg(t("appleCancel"));
                    } else {
                      setAppleMsg(t("appleFail"));
                      sfx.deny();
                    }
                  })();
                }}
              >
                <span>{t("signInApple")}</span>
                <span className="settings-val">{appleBusy ? "…" : t("link")}</span>
              </button>
              <p className="settings-hint">{t("appleHint")}</p>
              {appleMsg ? <p className="settings-hint">{appleMsg}</p> : null}
            </>
          )}
          <button
            type="button"
            className="settings-row"
            onClick={() => {
              void (async () => {
                setRestoreMsg(t("restoring"));
                const res = await restorePurchases();
                setRestoreMsg(
                  res.ok
                    ? t("restored")
                    : t("restoreFail", { reason: res.reason ?? "unknown" }),
                );
                sfx.ui();
              })();
            }}
          >
            <span>{t("restorePurchases")}</span>
            <span className="settings-val">{t("appleId")}</span>
          </button>
          {restoreMsg ? <p className="settings-hint">{restoreMsg}</p> : null}
          {adsPrivacyOptionsRequired() && (
            <button
              type="button"
              className="settings-row"
              onClick={() => void openAdPrivacyOptions()}
            >
              <span>{t("adPrivacy")}</span>
              <span className="settings-val">{t("manage")}</span>
            </button>
          )}
          <p className="settings-hint">
            {t("settingsHint", { n: GAME.maxJumpsHeld })}{" "}
            <a
              className="settings-link"
              href="https://worldvelopers.com/3dash/privacy.html"
              target="_blank"
              rel="noreferrer"
            >
              {t("privacy")}
            </a>
            {" · "}
            <a
              className="settings-link"
              href="https://worldvelopers.com/3dash/support.html"
              target="_blank"
              rel="noreferrer"
            >
              {t("support")}
            </a>
          </p>
        </div>
      )}

      {panel === "board" && (
        <div className="board">
          <p className="board-pseudo">
            {playerName.trim()
              ? t("playingAs", { name: playerName.trim() })
              : t("noPseudo")}
          </p>
          <p className="board-hint">{t("boardHint", { duel: t("duel") })}</p>
          {loadingBoard ? (
            <p className="board-empty">{t("loading")}</p>
          ) : board.length === 0 ? (
            <p className="board-empty">{t("noScores")}</p>
          ) : (
            <ol className="board-list">
              {board.map((row) => {
                const canDuel = Boolean(row.seed && isRunSeed(row.seed));
                return (
                  <li
                    key={`${row.playerId}-${row.rank}`}
                    className={canDuel ? "can-duel" : undefined}
                  >
                    <span className="rank">#{row.rank}</span>
                    <span className="who">{row.name}</span>
                    <span className="score">{row.distance} m</span>
                    {canDuel ? (
                      <button
                        type="button"
                        className="btn ghost small"
                        onClick={() => {
                          sfx.start();
                          music.setDrive(0);
                          startRun({
                            seed: row.seed,
                            duel: {
                              rivalName: row.name,
                              rivalDistance: row.distance,
                              seed: row.seed!,
                              difficulty,
                              stage: isStageId(row.stage) ? row.stage : equippedStageId,
                              mode: "ranks",
                            },
                          });
                        }}
                      >
                        {t("duel")}
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {panel === "garage" && (
        <div className="shop garage-panel">
          <div className="garage-tabs" role="tablist" aria-label={t("garage")}>
            <button
              type="button"
              role="tab"
              aria-selected={garageTab === "rides"}
              className={`garage-tab${garageTab === "rides" ? " on" : ""}`}
              onClick={() => {
                setGarageTab("rides");
                setGarageHint(null);
                sfx.ui();
              }}
            >
              {t("rides")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={garageTab === "worlds"}
              className={`garage-tab${garageTab === "worlds" ? " on" : ""}`}
              onClick={() => {
                setGarageTab("worlds");
                setGarageHint(null);
                sfx.ui();
              }}
            >
              {t("worlds")}
            </button>
          </div>

          {garageTab === "rides" ? (
            <>
              <div className="garage-nav">
                <button
                  type="button"
                  className="btn ghost small garage-arrow"
                  aria-label={t("prevVehicle")}
                  onClick={() => browseVehicle(-1)}
                >
                  ‹
                </button>
                <div
                  className="garage-info"
                  style={{ ["--c" as string]: preview.color }}
                >
                  <GaragePreview vehicle={preview} />
                  <h2 className="garage-name">{preview.name}</h2>
                  <p className="garage-blurb">{tVehicleBlurb(preview.id)}</p>
                  <div className="stat-bars">
                    {(
                      [
                        ["statSpeed", preview.stats.speed],
                        ["statJump", preview.stats.jump],
                        ["statCoins", preview.stats.coin],
                      ] as const
                    ).map(([key, value]) => (
                      <div key={key} className="stat-row">
                        <span>{t(key)}</span>
                        <div className="stat-track" aria-hidden>
                          {Array.from({ length: 5 }, (_, i) => (
                            <i key={i} className={i < value ? "on" : ""} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="garage-power">
                    {preview.power.type === "none"
                      ? t("noSpecialPower")
                      : `${preview.power.type === "passive" ? t("passive") : t("active")} · ${tPowerBlurb(preview.id, preview.power.id)}`}
                  </p>
                  {(() => {
                    const owned = vehiclesOwned.includes(preview.id);
                    const lockedIap =
                      preview.iapExclusive && !starterOwned && !owned;
                    const equipped = equippedVehicleId === preview.id;
                    const canAfford = coins >= preview.price;
                    const shortfall = Math.max(0, preview.price - coins);

                    if (owned) {
                      return (
                        <div className="garage-cta">
                          <button
                            type="button"
                            className={`btn small ${equipped ? "ghost equipped" : "primary"}`}
                            disabled={equipped}
                            onClick={() => {
                              equipVehicle(preview.id);
                              setBrowseId(preview.id);
                              sfx.ui();
                            }}
                          >
                            {equipped ? t("equipped") : t("equip")}
                          </button>
                          <button
                            type="button"
                            className="btn ghost small"
                            onClick={() => launchPractice(preview.id)}
                          >
                            {t("tryRide")}
                          </button>
                        </div>
                      );
                    }
                    if (lockedIap) {
                      return (
                        <div className="garage-cta">
                          <button
                            type="button"
                            className="btn ghost small locked"
                            onClick={() => {
                              setPanel("shop");
                              sfx.ui();
                            }}
                          >
                            {t("getInShop")}
                          </button>
                          <button
                            type="button"
                            className="btn ghost small"
                            onClick={() => launchPractice(preview.id)}
                          >
                            {t("tryRide")}
                          </button>
                          <p
                            className="garage-hint"
                            style={{ color: "var(--muted)" }}
                          >
                            {t("phantomPackShort", {
                              price: iapPriceLabel(IAP.STARTER_PACK),
                            })}
                          </p>
                        </div>
                      );
                    }
                    return (
                      <div
                        className={`garage-cta${ctaShake ? " shake" : ""}`}
                        onAnimationEnd={() => setCtaShake(false)}
                      >
                        <button
                          type="button"
                          className={`btn small ${canAfford ? "primary" : "ghost short"}`}
                          onClick={() => {
                            if (!canAfford) {
                              setGarageHint(t("needMore", { n: shortfall }));
                              setCtaShake(true);
                              sfx.deny();
                              return;
                            }
                            if (unlockVehicle(preview.id as VehicleId)) {
                              setGarageHint(null);
                              setBrowseId(preview.id);
                              sfx.coin();
                              void syncGameCenter({ runScore: 0 });
                            }
                          }}
                        >
                          {canAfford
                            ? t("buyPrice", { n: preview.price })
                            : t("haveCoins", { price: preview.price, have: coins })}
                        </button>
                        <button
                          type="button"
                          className="btn ghost small"
                          onClick={() => launchPractice(preview.id)}
                        >
                          {t("tryRide")}
                        </button>
                        {garageHint ? (
                          <p className="garage-hint" role="status">
                            {garageHint}
                          </p>
                        ) : canAfford ? null : (
                          <p className="garage-hint" role="status">
                            {t("needMore", { n: shortfall })}
                          </p>
                        )}
                        {!canAfford && (
                          <button
                            type="button"
                            className="btn ghost small"
                            onClick={() => {
                              setPanel("shop");
                              sfx.ui();
                            }}
                          >
                            {t("buyCoinsShop")}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  className="btn ghost small garage-arrow"
                  aria-label={t("nextVehicle")}
                  onClick={() => browseVehicle(1)}
                >
                  ›
                </button>
              </div>
              <div className="garage-dots" aria-hidden>
                {VEHICLES.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`garage-dot${v.id === preview.id ? " on" : ""}`}
                    style={{ ["--c" as string]: v.color }}
                    onClick={() => {
                      setBrowseId(v.id);
                      sfx.ui();
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="garage-nav">
                <button
                  type="button"
                  className="btn ghost small garage-arrow"
                  aria-label={t("prevWorld")}
                  onClick={() => browseStage(-1)}
                >
                  ‹
                </button>
                <div
                  className="garage-info"
                  style={{ ["--c" as string]: stagePreview.rail }}
                >
                  <div
                    className="stage-swatch"
                    style={{
                      background: `linear-gradient(145deg, ${stagePreview.fog} 0%, ${stagePreview.ground} 45%, ${stagePreview.buildingHueA} 100%)`,
                      borderColor: stagePreview.rail,
                    }}
                    aria-hidden
                  >
                    <span
                      className="stage-swatch-rail"
                      style={{ background: stagePreview.rail }}
                    />
                  </div>
                  <h2 className="garage-name">{tStageName(stagePreview.id)}</h2>
                  <p className="garage-blurb">{tStageBlurb(stagePreview.id)}</p>
                  <p className="garage-power">
                    {coinBoostLabel(stagePreview.coinMult)}
                    {stagePreview.jumpMult > 1.1
                      ? t("higherJump")
                      : stagePreview.holeChance > 0
                        ? t("jumpHoles")
                        : ""}
                  </p>
                  {(() => {
                    const owned = stagesOwned.includes(stagePreview.id);
                    const equipped = equippedStageId === stagePreview.id;
                    const canAfford = coins >= stagePreview.price;
                    const shortfall = Math.max(0, stagePreview.price - coins);

                    if (owned) {
                      return (
                        <div className="garage-cta">
                          <button
                            type="button"
                            className={`btn small ${equipped ? "ghost equipped" : "primary"}`}
                            disabled={equipped}
                            onClick={() => {
                              equipStage(stagePreview.id);
                              setBrowseStageId(stagePreview.id);
                              sfx.ui();
                            }}
                          >
                            {equipped ? t("equipped") : t("equip")}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div
                        className={`garage-cta${ctaShake ? " shake" : ""}`}
                        onAnimationEnd={() => setCtaShake(false)}
                      >
                        <button
                          type="button"
                          className={`btn small ${canAfford ? "primary" : "ghost short"}`}
                          onClick={() => {
                            if (!canAfford) {
                              setGarageHint(t("needMore", { n: shortfall }));
                              setCtaShake(true);
                              sfx.deny();
                              return;
                            }
                            if (unlockStage(stagePreview.id)) {
                              setGarageHint(null);
                              setBrowseStageId(stagePreview.id);
                              sfx.coin();
                            }
                          }}
                        >
                          {canAfford
                            ? t("buyPrice", { n: stagePreview.price })
                            : t("haveCoins", {
                                price: stagePreview.price,
                                have: coins,
                              })}
                        </button>
                        {garageHint ? (
                          <p className="garage-hint" role="status">
                            {garageHint}
                          </p>
                        ) : canAfford ? null : (
                          <p className="garage-hint" role="status">
                            {t("needMore", { n: shortfall })}
                          </p>
                        )}
                        {!canAfford && (
                          <button
                            type="button"
                            className="btn ghost small"
                            onClick={() => {
                              setPanel("shop");
                              sfx.ui();
                            }}
                          >
                            {t("buyCoinsShop")}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  className="btn ghost small garage-arrow"
                  aria-label={t("nextWorld")}
                  onClick={() => browseStage(1)}
                >
                  ›
                </button>
              </div>
              <div className="garage-dots" aria-hidden>
                {STAGES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`garage-dot${s.id === stagePreview.id ? " on" : ""}`}
                    style={{ ["--c" as string]: s.rail }}
                    onClick={() => {
                      setBrowseStageId(s.id);
                      sfx.ui();
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {panel === "shop" && (
        <div className="shop-panel">
          <h2 className="shop-title">{t("shopTitle")}</h2>
          <div className="iap-row" key={iapTick}>
            <article className={`shop-offer${removeAds ? " owned" : ""}`}>
              <div className="shop-offer-copy">
                <strong>{t("noAds")}</strong>
                <p>{t("noAdsBlurb")}</p>
              </div>
              {removeAds ? (
                <span className="shop-owned">{t("owned")}</span>
              ) : (
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => {
                    void purchaseRemoveAds().then((res) => {
                      sfx.ui();
                      setIapMsg(
                        res.ok
                          ? res.reason === "web-mock"
                            ? t("adsRemovedWeb")
                            : t("adsRemoved")
                          : iapFailCopy(res.reason),
                      );
                    });
                  }}
                >
                  {iapPriceLabel(IAP.REMOVE_ADS)}
                </button>
              )}
            </article>
            <article className={`shop-offer${starterOwned ? " owned" : ""}`}>
              <div className="shop-offer-copy">
                <strong>{t("phantomPack")}</strong>
                <p>
                  {t("phantomPackBlurb", { n: STARTER_PACK_COINS })}
                  {starterOwned ? t("phantomPackGranted") : ""}
                </p>
              </div>
              {starterOwned ? (
                <span className="shop-owned">{t("owned")}</span>
              ) : (
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => {
                    void purchaseStarterPack().then((res) => {
                      sfx.ui();
                      setIapMsg(
                        res.ok
                          ? res.reason === "web-mock"
                            ? t("starterUnlockedWeb")
                            : t("starterUnlocked")
                          : iapFailCopy(res.reason),
                      );
                    });
                  }}
                >
                  {iapPriceLabel(IAP.STARTER_PACK)}
                </button>
              )}
            </article>
            <article className="shop-offer">
              <div className="shop-offer-copy">
                <strong>{t("coinPouch")}</strong>
                <p>{t("coinPackBlurb", { n: COIN_PACKS[IAP.COINS_SMALL] })}</p>
              </div>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => {
                  void purchaseCoinPack(IAP.COINS_SMALL).then((res) => {
                    sfx.ui();
                    const n = COIN_PACKS[IAP.COINS_SMALL];
                    setIapMsg(
                      res.ok
                        ? t("coinsGranted", {
                            n,
                            balance: useGame.getState().coins,
                          })
                        : iapFailCopy(res.reason),
                    );
                    if (res.ok) sfx.coin();
                  });
                }}
              >
                {iapPriceLabel(IAP.COINS_SMALL)}
              </button>
            </article>
            <article className="shop-offer">
              <div className="shop-offer-copy">
                <strong>{t("coinChest")}</strong>
                <p>{t("coinPackBlurb", { n: COIN_PACKS[IAP.COINS_LARGE] })}</p>
              </div>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => {
                  void purchaseCoinPack(IAP.COINS_LARGE).then((res) => {
                    sfx.ui();
                    const n = COIN_PACKS[IAP.COINS_LARGE];
                    setIapMsg(
                      res.ok
                        ? t("coinsGranted", {
                            n,
                            balance: useGame.getState().coins,
                          })
                        : iapFailCopy(res.reason),
                    );
                    if (res.ok) sfx.coin();
                  });
                }}
              >
                {iapPriceLabel(IAP.COINS_LARGE)}
              </button>
            </article>
            <button
              type="button"
              className="btn ghost small"
              onClick={() =>
                void restorePurchases().then((res) => {
                  sfx.ui();
                  setIapMsg(
                    res.ok
                      ? res.reason === "web-noop"
                        ? t("restoreIos")
                        : t("restored")
                      : t("restoreFail", { reason: res.reason ?? "error" }),
                  );
                })
              }
            >
              {t("restorePurchases")}
            </button>
          </div>
          {iapMsg ? (
            <p className="garage-hint" role="status" style={{ marginTop: 4 }}>
              {iapMsg}
            </p>
          ) : null}
        </div>
      )}
      </div>

      <div className="menu-footer">
        <div className="menu-actions">
          <button
            type="button"
            className="btn ghost"
            onClick={() => setPanel((p) => (p === "board" ? "none" : "board"))}
          >
            {panel === "board" ? t("close") : t("ranks")}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              panel === "garage" ? setPanel("none") : openGarage()
            }
          >
            {panel === "garage" ? t("close") : t("garage")}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setPanel((p) => (p === "shop" ? "none" : "shop"))}
          >
            {panel === "shop" ? t("close") : t("shop")}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              setPanel((p) => (p === "settings" ? "none" : "settings"))
            }
          >
            {panel === "settings" ? t("close") : t("settings")}
          </button>
        </div>
        {panel === "none" && (
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              sfx.start();
              music.setDrive(0);
              startRun();
            }}
          >
            {pendingDuel ? t("duel") : t("dash")}
          </button>
        )}
      </div>
    </div>
  );
}

function DailyBlock() {
  const ensureDaily = useGame((s) => s.ensureDaily);
  const streakCount = useGame((s) => s.streakCount);
  const streakLastDay = useGame((s) => s.streakLastDay);
  const streakClaimedDay = useGame((s) => s.streakClaimedDay);
  const dailyProgress = useGame((s) => s.dailyProgress);
  const dailyClaimed = useGame((s) => s.dailyClaimed);
  const claimStreak = useGame((s) => s.claimStreak);
  const claimMission = useGame((s) => s.claimMission);
  const [toast, setToast] = useState<string | null>(null);
  const hydrated = useGame((s) => s.hydrated);
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem("lanedash.dailies.open") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!hydrated) return;
    ensureDaily();
  }, [ensureDaily, hydrated]);

  const today = localDayKey();
  const missions = missionsForDay(today);
  const streakReady =
    streakCount > 0 &&
    streakLastDay === today &&
    streakClaimedDay !== today;
  const streakAmt = streakReward(Math.max(1, streakCount));
  const doneCount = missions.filter((m) => {
    const progress = dailyProgress[m.id] ?? 0;
    return progress >= m.target;
  }).length;
  const claimable = missions.filter((m) => {
    const progress = dailyProgress[m.id] ?? 0;
    return progress >= m.target && !dailyClaimed.includes(m.id);
  }).length;

  useEffect(() => {
    if (claimable > 0 || streakReady) setOpen(true);
  }, [claimable, streakReady]);

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("lanedash.dailies.open", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
    sfx.ui();
  };

  return (
    <div
      className={`daily-block${open ? "" : " collapsed"}${claimable > 0 || streakReady ? " has-claim" : ""}`}
    >
      <div className="daily-streak">
        <span className="daily-streak-day">
          {t("dayN", {
            n: Math.max(streakCount, streakLastDay === today ? streakCount : 0),
          })}
        </span>
        <button
          type="button"
          className={`btn small ${streakReady ? "claim" : "ghost"}`}
          disabled={!streakReady}
          onClick={() => {
            const n = claimStreak();
            if (n > 0) {
              sfx.coin();
              setToast(t("streakToast", { n }));
              void syncGameCenter({ runScore: 0 });
            } else sfx.deny();
          }}
        >
          {streakClaimedDay === today
            ? t("claimed")
            : streakReady
              ? t("streakClaim", { n: streakAmt })
              : t("playToStreak")}
        </button>
      </div>
      <button
        type="button"
        className={`daily-toggle${claimable > 0 ? " ready" : ""}`}
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <span>
          {t("missions", { done: doneCount, total: missions.length })}
          {claimable > 0 ? t("missionsReady") : ""}
        </span>
        <span className="daily-chevron" aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open && (
        <ul className="daily-missions">
          {missions.map((m) => {
            const progress = dailyProgress[m.id] ?? 0;
            const done = progress >= m.target;
            const claimed = dailyClaimed.includes(m.id);
            const shown = Math.min(progress, m.target);
            return (
              <li
                key={m.id}
                className={`${done ? "done" : ""}${done && !claimed ? " claimable" : ""}${claimed ? " claimed" : ""}`}
              >
                <span className="daily-mission-label">
                  {claimed ? "✓ " : done ? "● " : "○ "}
                  {tMission(m.id)}
                  <em>
                    {" "}
                    {shown}/{m.target}
                  </em>
                </span>
                <button
                  type="button"
                  className={`btn small ${done && !claimed ? "claim mission-claim" : "ghost"}`}
                  disabled={!done || claimed}
                  onClick={() => {
                    const n = claimMission(m.id);
                    if (n > 0) {
                      sfx.coin();
                      setToast(t("missionToast", { n: MISSION_REWARD }));
                    } else sfx.deny();
                  }}
                >
                  {claimed
                    ? t("missionDone")
                    : done
                      ? t("missionClaim", { n: MISSION_REWARD })
                      : "…"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {toast ? <p className="daily-toast">{toast}</p> : null}
    </div>
  );
}

function ContinueOverlay() {
  const doContinue = useGame((s) => s.useContinue);
  const skipContinue = useGame((s) => s.skipContinue);
  const showContinue = useGame((s) => s.showContinue);
  const continuesUsed = useGame((s) => s.continuesUsed);
  const removeAds = useGame((s) => s.removeAds);
  const runsSinceAd = useGame((s) => s.runsSinceAd);
  const setRunsSinceAd = useGame((s) => s.setRunsSinceAd);
  const duel = useGame((s) => s.duel);
  const practiceVehicle = useGame((s) => s.practiceVehicle);
  const [adFail, setAdFail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const freeContinue = continuesUsed === 0 || removeAds;
  const remaining = Math.max(0, GAME.maxContinuesPerRun - continuesUsed);

  useEffect(() => {
    void hideMenuBanner();
    void preloadRewardedAd({ force: true });
  }, []);

  const endRun = async () => {
    skipContinue();
    if (practiceVehicle) return;
    const next = await maybeShowInterstitial(removeAds, runsSinceAd);
    setRunsSinceAd(next);
  };

  const onContinue = async () => {
    if (freeContinue) {
      sfx.continue();
      doContinue();
      return;
    }
    setBusy(true);
    setAdFail(null);
    try {
      const res = await playRewardedAd();
      if (!res.ok) {
        setAdFail(res.reason);
        return;
      }
      sfx.continue();
      doContinue();
    } finally {
      setBusy(false);
    }
  };

  const failCopy =
    adFail === "consent"
      ? t("adsBlocked")
      : adFail === "closed"
        ? t("watchWholeAd")
        : adFail
          ? t("adUnavailable")
          : null;

  return (
    <div
      className="overlay dim"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      <h2>{t("crash")}</h2>
      {showContinue ? (
        <p>
          {freeContinue
            ? continuesUsed === 0
              ? t("firstContinue")
              : t("adsRemovedContinue")
            : t("watchAd")}{" "}
          {t("leftCount", { n: remaining })}
        </p>
      ) : (
        <p>
          {practiceVehicle
            ? t("practiceCrash")
            : duel
              ? t("duelCrash")
              : t("outOfContinues")}
        </p>
      )}
      {failCopy ? <p className="rank-line">{failCopy}</p> : null}
      {showContinue && (
        <button
          type="button"
          className="btn primary"
          disabled={busy}
          onClick={() => void onContinue()}
        >
          {busy
            ? t("loadingAd")
            : adFail
              ? t("continueRetry")
              : freeContinue
                ? t("continueFree")
                : t("continueAd")}
        </button>
      )}
      <button
        type="button"
        className={showContinue ? "btn ghost" : "btn primary"}
        disabled={busy}
        onClick={() => void endRun()}
      >
        {t("endRun")}
      </button>
    </div>
  );
}

function ResultOverlay() {
  const distance = useGame((s) => s.distance);
  const coinsRun = useGame((s) => s.coinsRun);
  const difficulty = useGame((s) => s.difficulty);
  const best = useGame((s) => s.bestByDifficulty[s.difficulty]);
  const startRun = useGame((s) => s.startRun);
  const backToMenu = useGame((s) => s.backToMenu);
  const queueHotseat = useGame((s) => s.queueHotseat);
  const grantRewardedCoins = useGame((s) => s.grantRewardedCoins);
  const leaderboardId = useGame((s) => s.leaderboardId);
  const playerName = useGame((s) => s.playerName);
  const setPlayerName = useGame((s) => s.setPlayerName);
  const appleUserId = useGame((s) => s.appleUserId);
  const nameChosen = useGame((s) => s.nameChosen);
  const isBest = useGame((s) => s.lastRunWasBest);
  const runSeed = useGame((s) => s.runSeed);
  const equippedStageId = useGame((s) => s.equippedStage);
  const duel = useGame((s) => s.duel);
  const duelOutcome = useGame((s) => s.duelOutcome);
  const practiceVehicle = useGame((s) => s.practiceVehicle);
  const savedName = playerName.trim() || (appleUserId ? "Courier" : "");
  const skipNamePrompt = Boolean(nameChosen || appleUserId || savedName);
  const [needsName, setNeedsName] = useState(!skipNamePrompt);
  const [nameDraft, setNameDraft] = useState("");
  const removeAds = useGame((s) => s.removeAds);
  const [rankInfo, setRankInfo] = useState<string>(
    skipNamePrompt ? t("submitting") : t("pickPseudo"),
  );
  const lastSubmittedName = useRef<string | null>(null);
  const introPlayed = useRef(false);

  useEffect(() => {
    if (skipNamePrompt) setNeedsName(false);
  }, [skipNamePrompt]);

  useEffect(() => {
    void showMenuBanner(removeAds);
  }, [removeAds]);

  const pushScore = useCallback(
    (rawName: string) => {
      const name = rawName.trim() || "Courier";
      // Skip identical re-submit (Play again / Menu).
      if (lastSubmittedName.current === name) return;
      lastSubmittedName.current = name;
      setRankInfo(t("submitting"));
      void submitScore({
        playerId: leaderboardId(),
        name,
        distance: Math.floor(distance),
        difficulty: difficulty as DifficultyId,
        seed: runSeed,
        stage: duel?.stage ?? equippedStageId,
      }).then((res) => {
        if (!res) {
          setRankInfo(t("offlineScore"));
          return;
        }
        setRankInfo(
          res.improved
            ? t("rankImproved", { rank: res.rank, total: res.total })
            : t("rankBestStill", { best: res.best, rank: res.rank }),
        );
      });
    },
    [distance, difficulty, leaderboardId, runSeed, equippedStageId, duel],
  );

  /** One-time: save pseudo then submit. Later edits live in Settings. */
  const savePseudo = useCallback(() => {
    const clean = nameDraft.trim();
    if (!clean) return false;
    setPlayerName(clean);
    setNeedsName(false);
    pushScore(clean);
    sfx.ui();
    return true;
  }, [nameDraft, pushScore, setPlayerName]);

  useEffect(() => {
    if (introPlayed.current) return;
    introPlayed.current = true;
    sfx.gameOver();
    music.setDrive(0);
    if (practiceVehicle) return;
    if (savedName) {
      if (!playerName.trim() && appleUserId) setPlayerName(savedName);
      pushScore(savedName);
    }
    void syncGameCenter({
      runScore: Math.floor(distance),
      difficulty: difficulty as DifficultyId,
    });
  }, [savedName, pushScore, playerName, appleUserId, setPlayerName, practiceVehicle, distance, difficulty]);

  return (
    <div
      className="overlay dim"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      {practiceVehicle ? (
        <>
          <h2>{t("practiceOver")}</h2>
          <p className="result-score">{Math.floor(distance)} m</p>
          <p>{t("practiceNote")}</p>
          <button
            type="button"
            className="btn primary"
            onClick={() => {
              sfx.start();
              startRun({ practiceVehicle });
            }}
          >
            {t("tryAgain")}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={() => backToMenu()}
          >
            {t("backGarage")}
          </button>
        </>
      ) : (
        <>
      <h2>
        {duel
          ? duelOutcome === "win"
            ? t("theyCrashed")
            : duelOutcome === "lose"
              ? t("youCrashed")
              : duelOutcome === "draw"
                ? t("deadHeat")
                : t("duelOver")
          : isBest
            ? t("newBest")
            : t("runOver")}
      </h2>
      <p className="result-score">{Math.floor(distance)} m</p>
      {duel ? (
        <p>
          {t("vsLine", { name: duel.rivalName, m: duel.rivalDistance })}
          {duelOutcome === "win"
            ? t("vsLasted")
            : duelOutcome === "lose"
              ? t("vsHeld")
              : t("vsSame")}
        </p>
      ) : null}
      <p>
        {t("resultCoins", {
          coins: coinsRun,
          best,
          diff: tDiff(difficulty as DifficultyId),
        })}
      </p>
      <p className="rank-line">{rankInfo}</p>
      {needsName ? (
        <label className="name-field inline">
          <span>{t("choosePseudo")}</span>
          <input
            value={nameDraft}
            maxLength={16}
            placeholder="Courier"
            autoFocus
            enterKeyHint="done"
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
                savePseudo();
              }
            }}
          />
          <button
            type="button"
            className="btn ghost name-save"
            disabled={!nameDraft.trim()}
            onClick={() => savePseudo()}
          >
            {t("savePseudo")}
          </button>
        </label>
      ) : (
        <p className="result-pseudo">
          {t("asPlayer", { name: playerName.trim() || "Courier" })}
          <span className="result-pseudo-hint">{t("editSettings")}</span>
        </p>
      )}
      <button
        type="button"
        className="btn primary"
        onClick={() => {
          if (needsName && !savePseudo()) return;
          sfx.start();
          if (duel) {
            startRun({ seed: duel.seed, duel });
          } else {
            startRun();
          }
        }}
      >
        {duel ? t("rematch") : t("playAgain")}
      </button>
      <div className="result-twop">
        <p className="result-twop-label">{t("resultTwoP")}</p>
        {Math.floor(distance) >= 1 ? (
          <button
            type="button"
            className="btn ghost"
            onClick={() => {
              if (needsName && !savePseudo()) return;
              queueHotseat();
              backToMenu();
              sfx.ui();
            }}
          >
            {t("passPhone")}
          </button>
        ) : null}
        <button
          type="button"
          className="btn ghost"
          onClick={() =>
            void shareRun({
              distance,
              coins: coinsRun,
              difficulty: tDiff(difficulty as DifficultyId),
              name: playerName,
              seed: runSeed,
              stage: duel?.stage ?? equippedStageId,
              difficultyId: difficulty as DifficultyId,
            })
          }
        >
          {t("shareInvite")}
        </button>
      </div>
      <button
        type="button"
        className="btn ghost"
        onClick={async () => {
          const res = await playRewardedAd();
          if (res.ok) grantRewardedCoins(25);
        }}
      >
        {t("coinsPlus", { n: 25 })}
      </button>
      <button
        type="button"
        className="btn ghost"
        onClick={() => {
          if (needsName) savePseudo();
          backToMenu();
        }}
      >
        {t("menu")}
      </button>
        </>
      )}
    </div>
  );
}
